import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateField, AmountField, PriceField, CryptoField, TransactionHashField, NotesField } from '../molecules/FormFields';
import { Button } from '../atoms/Button';
import { useCryptoPriceAtDate } from '../../utils/hooks/useCryptoPriceAtDate';
import { usePortfolioValueAtDate } from '../../utils/hooks/usePortfolioValueAtDate';
import { formatCurrency } from '../../utils/date';
import type { PayoutType } from '../../types/payout';

/**
 * Simple transaction fields component for use within FormProvider
 * Used for payout, achat, vente, and stacking transaction types
 */
interface SimpleTransactionFieldsProps {
  prefix?: string;
  transactionType: Exclude<PayoutType, 'swap'>;
  isFetchingPrice?: boolean;
  /** Valeur déjà enregistrée pour cette transaction (édition) — passée par le parent
   *  directement depuis `editingPayout`, pas lue via `getValues()`, pour éviter une race
   *  condition avec le `reset()` du formulaire qui n'a pas encore tourné au montage. */
  initialUnitPrice?: number;
  initialPortfolioValueAtSale?: number;
  /** Idem : date/crypto de la transaction éditée, dérivées d'`editingPayout` par le parent —
   *  utilisées pour détecter un changement de date/crypto depuis l'ouverture sans dépendre de
   *  `watch()` au montage, qui peut encore refléter l'état précédent le temps que `reset()` tourne. */
  initialDate?: string;
  initialCryptoId?: string;
}

export const SimpleTransactionFields: React.FC<SimpleTransactionFieldsProps> = ({
  prefix = '',
  transactionType,
  initialUnitPrice,
  initialPortfolioValueAtSale,
  initialDate,
  initialCryptoId,
}) => {
  const { watch, setValue } = useFormContext();

  // Build field names with optional prefix — memoïsé pour stabiliser les deps useEffect
  const fieldName = useCallback(
    (field: string) => prefix ? `${prefix}.${field}` : field,
    [prefix]
  );

  // Watch form values for real-time calculations and price fetching
  const crypto = watch(fieldName('crypto'));
  const date = watch(fieldName('date'));
  const amount = watch(fieldName('amount')) ?? 0;
  const unitPrice = watch(fieldName('unitPrice')) ?? 0;
  const purchaseAmount = watch(fieldName('purchaseAmount')) ?? 0;

  // Fetch crypto price automatically when crypto or date changes
  const { data: cryptoPrice, isLoading: isPriceLoading } = useCryptoPriceAtDate({
    coinId: crypto?.id || '',
    date: date || '',
    enabled: !!crypto && !!date && transactionType !== 'achat', // Don't auto-fetch for purchases
  });

  // Ni la case 212 ni le prix de vente unitaire ne doivent se recalculer à la simple ouverture
  // d'une transaction existante (consultation) — seulement à la création, quand la date/crypto
  // change, ou sur demande manuelle. `initialUnitPrice`/`initialPortfolioValueAtSale` viennent
  // du parent (dérivés directement d'`editingPayout`, dans le même rendu que le montage de ce
  // composant), pas de `getValues()`/`watch()` ici : ces derniers reflètent encore l'ancien état
  // du formulaire au moment du montage, car le `reset()` du parent ne tourne que dans un effet
  // qui se déclenche après ce montage — les lire ici capturerait une valeur périmée.
  const initialDateRef = useRef(initialDate ?? date);
  const initialCryptoIdRef = useRef(initialCryptoId ?? crypto?.id);
  const [manualRecalcRequested, setManualRecalcRequested] = useState(false);

  const dateChangedSinceOpen = date !== initialDateRef.current;
  const cryptoChangedSinceOpen = crypto?.id !== initialCryptoIdRef.current;

  // À la création d'une vente, ne lancer le calcul de la case 212 qu'une fois que l'utilisateur
  // a réellement saisi une crypto ET une date : la date est vide par défaut pour les ventes
  // (cf. PayoutFormRHF), donc sans ce garde on lancerait un précalcul sur une date par défaut
  // probablement fausse. En édition, le garde ne fait que refléter l'état déjà attendu.
  const hasCryptoAndDate = Boolean(crypto?.id) && Boolean(date);

  const hasStoredPortfolioValue =
    initialPortfolioValueAtSale !== undefined &&
    initialPortfolioValueAtSale !== null &&
    Number(initialPortfolioValueAtSale) > 0;
  const shouldCalculatePortfolioValue =
    transactionType === 'vente' &&
    hasCryptoAndDate &&
    (!hasStoredPortfolioValue || dateChangedSinceOpen || manualRecalcRequested);

  const hasStoredUnitPrice =
    initialUnitPrice !== undefined && initialUnitPrice !== null && Number(initialUnitPrice) > 0;
  const shouldAutoFillUnitPrice =
    transactionType !== 'achat' && (!hasStoredUnitPrice || dateChangedSinceOpen || cryptoChangedSinceOpen);

  // Calcule la valeur du portefeuille à la date de la vente (case 212)
  const {
    totalValue: portfolioValue,
    totalValueWithCustomCostBasis,
    customHoldings,
    failedHoldings,
    isLoading: isPortfolioLoading,
    priceProgress: portfolioPriceProgress,
    refetch: refetchPortfolioValue,
  } = usePortfolioValueAtDate({
    date: date || '',
    enabled: shouldCalculatePortfolioValue && !!date,
  });

  // Update form when price is fetched — mais pas si une valeur était déjà enregistrée pour
  // cette transaction et que ni la date ni la crypto n'ont changé depuis l'ouverture (sinon
  // on écraserait un prix de vente réel avec le prix de marché à la simple consultation).
  useEffect(() => {
    if (cryptoPrice !== null && cryptoPrice !== undefined && shouldAutoFillUnitPrice) {
      setValue(fieldName('unitPrice'), cryptoPrice);
    }
  }, [cryptoPrice, fieldName, setValue, shouldAutoFillUnitPrice]);

  // Pré-remplit la case 212 dès que la valeur du portefeuille est calculée — gardé explicitement
  // par `shouldCalculatePortfolioValue` en plus du flag `enabled` passé au hook ci-dessus, pour ne
  // pas dépendre uniquement du hook renvoyant une valeur nulle/à 0 quand il est désactivé.
  useEffect(() => {
    if (
      shouldCalculatePortfolioValue &&
      portfolioValue !== null &&
      portfolioValue !== undefined &&
      portfolioValue > 0
    ) {
      setValue(fieldName('portfolioValueAtSale'), Math.round(portfolioValue * 100) / 100);
    }
  }, [portfolioValue, fieldName, setValue, shouldCalculatePortfolioValue]);

  // Calculate total value based on transaction type
  const price = transactionType === 'achat' ? purchaseAmount : unitPrice;
  const totalValue = Number(amount) * Number(price);

  // Get field label based on transaction type
  const getPriceFieldLabel = () => {
    switch (transactionType) {
      case 'payout':
      case 'stacking':
        return 'Prix unitaire reçu';
      case 'achat':
        return 'Montant total d\'achat';
      case 'vente':
        return 'Prix de vente unitaire';
      default:
        return 'Prix unitaire';
    }
  };


  return (
    <>
      {/* Cryptocurrency selection */}
      <CryptoField
        name={fieldName('crypto')}
        label="Cryptomonnaie"
        required
        placeholder="Sélectionnez une cryptomonnaie"
      />

      {/* Date, Amount, and Price grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <DateField
          name={fieldName('date')}
          label="Date"
          required
          type="datetime-local"
        />

        <AmountField
          name={fieldName('amount')}
          label="Quantité"
          required
          step="0.00000001"
          placeholder="Entrez la quantité"
        />

        <PriceField
          name={fieldName(transactionType === 'achat' ? 'purchaseAmount' : 'unitPrice')}
          label={getPriceFieldLabel()}
          required
          placeholder="Entrez le prix"
          isFetching={isPriceLoading}
        />
      </div>

      {/* Case 212 — valeur totale du portefeuille au moment de la cession (requis pour le formulaire 2086) */}
      {transactionType === 'vente' && (
        <div className="space-y-2">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <PriceField
                name={fieldName('portfolioValueAtSale')}
                label="Valeur totale du portefeuille en € (case 212 — formulaire 2086)"
                required
                placeholder={isPortfolioLoading ? 'Calcul en cours…' : 'Ex: 3454.00'}
                isFetching={isPortfolioLoading}
                fetchingProgress={portfolioPriceProgress}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={() => {
                setManualRecalcRequested(true);
                // `enabled` seul ne suffit pas : si le calcul automatique a déjà tourné (même
                // avec des échecs), la requête agrégée est déjà "fraîche" et ne se relance pas
                // toute seule — il faut forcer explicitement un nouveau `queryFn`.
                void refetchPortfolioValue();
              }}
              disabled={isPortfolioLoading}
            >
              Recalculer
            </Button>
          </div>

          {/* Valeur enregistrée, pas encore recalculée dans cette session — évite de brûler des
              appels API / d'écraser silencieusement la valeur juste pour une consultation */}
          {!shouldCalculatePortfolioValue && !isPortfolioLoading && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Valeur déjà enregistrée pour cette vente. Cliquez sur « Recalculer » pour la recalculer depuis les cryptos détenues à cette date.
            </p>
          )}

          {/* Hint quand le calcul est ok sans tokens custom ni échec de prix */}
          {shouldCalculatePortfolioValue &&
            !isPortfolioLoading &&
            portfolioValue !== null &&
            customHoldings.length === 0 &&
            failedHoldings.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Calculé automatiquement depuis les cryptos détenues à cette date — modifiable
              </p>
            )}

          {/* Warning : prix indisponibles (ex: throttling API) — la case 212 est incomplète */}
          {shouldCalculatePortfolioValue && !isPortfolioLoading && failedHoldings.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <p className="font-medium text-red-800 mb-2">
                Prix indisponible pour {failedHoldings.length === 1 ? 'cette crypto' : 'ces cryptos'} (API temporairement indisponible)&nbsp;: la valeur ci-dessus est incomplète, vérifiez-la avant de valider.
              </p>
              <ul className="space-y-1">
                {failedHoldings.map((h) => (
                  <li key={h.cryptoId} className="text-red-700">
                    <span className="font-semibold">{h.name}</span>
                    <span className="text-red-500 ml-1">({h.symbol})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warning tokens custom */}
          {!isPortfolioLoading && customHoldings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <p className="font-medium text-amber-800 mb-2">
                Tokens sans prix de marché exclus du calcul automatique&nbsp;:
              </p>
              <ul className="space-y-1 mb-2">
                {customHoldings.map((h) => (
                  <li key={h.cryptoId} className="flex items-baseline justify-between text-amber-700">
                    <span>
                      <span className="font-semibold">{h.name}</span>
                      <span className="text-amber-500 ml-1">({h.symbol})</span>
                      <span className="ml-2 text-xs">{h.amount.toFixed(8)}</span>
                    </span>
                    <span className="font-medium ml-4 whitespace-nowrap">
                      ≈&nbsp;{h.costBasisValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} au coût d&apos;achat
                    </span>
                  </li>
                ))}
              </ul>
              {totalValueWithCustomCostBasis !== null && (
                <p className="text-xs text-amber-600 border-t border-amber-200 pt-2">
                  Si on inclut ces tokens au coût d&apos;acquisition, la case 212 serait&nbsp;
                  <strong>
                    {totalValueWithCustomCostBasis.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </strong>
                  . Corrigez le champ si vous disposez de la valeur de marché réelle.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transaction hash (optional) */}
      <TransactionHashField
        name={fieldName('transactionHash')}
      />

      {/* Notes (optional) */}
      <NotesField
        name={fieldName('notes')}
      />

      {/* Total value summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-600 font-medium mb-1">Valeur totale</p>
            <p className="text-2xl font-bold text-blue-900">
              {formatCurrency(totalValue)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-blue-600">Calculé automatiquement</p>
          </div>
        </div>
      </div>
    </>
  );
};
